import { EditOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Flex,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  useClearAllPersistedSegments,
  useDeletePersistedSegments,
  useSavedSegments,
  useUpdatePersistedSegment,
} from '../../../../api/Segments';
import { QueryKeys, SegmentType } from '../../../../constants';
import useParams from '../../../../hooks/useParams';
import { BluePrimary, Gray2 } from '../../../../styles';
import { queryType, questionType, segmentType } from '../../../../types';
import CreateSegmentModal from '../../CreateSegmentModal';
import EditSegmentModal from '../../EditSegmentModal';
import ManageSavedSegmentsModal from './ManageSavedSegmentsModal';
import SaveSegmentModal from './SaveSegmentModal';

const { Text, Link, Title } = Typography;

const StyledTag = styled(Tag)`
  margin-top: 8px;
  cursor: pointer;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledText = styled(Text)`
  color: ${(props) => (props.active ? Gray2 : '')};
`;

function SegmentThemesCard({
  segments = [],
  questions,
  questionResponseOptions,
  selectedQuestion,
  selectedSegmentQuestionId = undefined,
  setSelectedSegmentQuestionId,
  statAssistEnabled,
  setStatAssistEnabled,
  createPersistedSegments,
}) {
  const { surveyId } = useParams();
  const [isCreateSegmentModalVisible, setCreateSegmentModalVisible] = useState(false);
  const [isEditSegmentModalVisible, setEditSegmentModalVisible] = useState(false);
  const [isManageSavedSegmentsModalVisible, setManageSavedSegmentsModalVisible] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(undefined);
  const [segmentToSave, setSegmentToSave] = useState(null);

  const queryClient = useQueryClient();

  const { data: savedSegments, isLoading: savedSegmentsLoading } = useSavedSegments({ surveyId });

  const { mutate: updatePersistedSegment } = useUpdatePersistedSegment();
  const { mutate: deletePersistedSegments } = useDeletePersistedSegments();

  const { mutate: clearAllPersistedSegments } = useClearAllPersistedSegments({
    onMutate: async () => {
      const segmentQuestionId = selectedSegmentQuestionId;
      setSelectedSegmentQuestionId(undefined);
      await queryClient.cancelQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      await queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], (prev) => {
        const allRespondentSegment = prev.data.find(
          (s) => s.segment_type === SegmentType.ALL_RESPONDENTS,
        );
        return {
          ...prev,
          data: [{ ...allRespondentSegment, active: true }],
        };
      });
      return { prevSegments, segmentQuestionId };
    },
    onError: (_error, _variables, context) => {
      setSelectedSegmentQuestionId(context.segmentQuestionId);
      queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], context.prevSegments);
      message.error('Something went wrong clearing segments');
    },
  });

  const handleSelectSegmentQuestion = async (questionId) => {
    setSelectedSegmentQuestionId(questionId);

    // deactivate the all respondents segment if it's active
    const allRespondentSegment = segments.find(
      (s) => s.segment_type === SegmentType.ALL_RESPONDENTS,
    );
    if (allRespondentSegment.active) {
      updatePersistedSegment({
        surveyId,
        segmentId: allRespondentSegment.id,
        data: { active: false },
      });
    }

    // delete all existing attribute segments
    const attributeSegments = segments.filter((s) => s.segment_type === SegmentType.ATTRIBUTE);
    if (attributeSegments.length) {
      deletePersistedSegments({ surveyId, segmentIds: attributeSegments.map((s) => s.id) });
    }

    // add each response option as a separate segment
    const responseOptions = questionResponseOptions.find((q) => q.data.questionId === questionId);
    const data = responseOptions?.data.data.map((option) => ({
      active: true,
      segment: { [questionId]: [option.text] },
      segment_type: SegmentType.ATTRIBUTE,
      label: option.text,
    }));
    createPersistedSegments({ surveyId, data });
  };

  const handleSaveCustomSegment = async (selectedChoices, label = null) => {
    createPersistedSegments({
      surveyId,
      data: [
        {
          active: true,
          segment: selectedChoices,
          segment_type: SegmentType.CUSTOM,
          label: label || Object.values(selectedChoices).flat().join(', '),
        },
      ],
    });
  };

  const options = questions
    .reduce(
      (acc, { text, id }) => {
        const group = selectedQuestion.significant_segments.includes(id)
          ? acc[0].options
          : acc[1].options;
        group.push({ label: text, value: id });
        return acc;
      },
      [
        { label: 'May contain meaningful theme variation', options: [] },
        { label: 'No meaningful theme variation', options: [] },
      ],
    )
    .filter((group) => group.options.length);

  const createSegmentItems = [
    {
      key: 'create',
      label: 'Create custom segment',
      onClick: () => {
        setCreateSegmentModalVisible(true);
      },
    },
    {
      key: 'saved',
      label: 'Saved segments',
      popupClassName: 'saved-segments-submenu',
      children: [
        ...(savedSegments?.data.map((s) => ({
          key: s.id,
          label: (
            <Text ellipsis={{ tooltip: s.label }} style={{ maxWidth: '270px' }}>
              {s.label}
            </Text>
          ),
          onClick: () => handleSaveCustomSegment(s.segment, s.label),
        })) || []),
        { type: 'divider' },
        {
          key: 'manage',
          label: 'Manage saved segments',
          onClick: () => setManageSavedSegmentsModalVisible(true),
        },
      ],
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="baseline">
        <Title level={5}>Segment themes</Title>
        <Link
          href="https://intercom.help/fathomthat/en/articles/6368747-creating-segments"
          target="_blank"
          rel="noreferrer noopener"
        >
          How to use segments
        </Link>
      </Flex>
      <Card size="small">
        <Row gutter={8} wrap={false}>
          <Col flex="auto">
            <StyledSelect
              placeholder="Please select a closed ended or demographic question"
              value={selectedSegmentQuestionId}
              onChange={handleSelectSegmentQuestion}
              options={options}
            />
            <Row>
              {segments.map((segment) => (
                <StyledTag key={segment.id} color={segment.active ? BluePrimary : ''}>
                  <Space>
                    <StyledText
                      active={segment.active ? 1 : 0}
                      onClick={() =>
                        updatePersistedSegment({
                          surveyId,
                          segmentId: segment.id,
                          data: { active: !segment.active },
                        })
                      }
                    >
                      {segment.label}
                    </StyledText>
                    <EditOutlined
                      onClick={() => {
                        setSelectedSegment(segment);
                        setEditSegmentModalVisible(true);
                      }}
                    />
                  </Space>
                </StyledTag>
              ))}
            </Row>
          </Col>
          <Col flex="none">
            <Flex vertical align="end" gap={8}>
              <Space>
                {savedSegments?.data.length ? (
                  <Dropdown menu={{ items: createSegmentItems }} trigger={['hover', 'click']}>
                    <Button
                      type="primary"
                      onClick={(e) => e.preventDefault()}
                      loading={savedSegmentsLoading}
                    >
                      Custom segments
                    </Button>
                  </Dropdown>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      setCreateSegmentModalVisible(true);
                    }}
                  >
                    Custom segments
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={() => clearAllPersistedSegments({ surveyId })}
                  danger
                  disabled={segments.length <= 1}
                >
                  Clear all
                </Button>
              </Space>
              {selectedQuestion.significant_segments.includes(selectedSegmentQuestionId) &&
                segments.every((s) =>
                  s.active ? s.segment_type === SegmentType.ATTRIBUTE : true,
                ) && (
                  <Space data-cy="stat-assist-toggle">
                    Stat Assist
                    <Switch
                      size="small"
                      checked={statAssistEnabled}
                      onChange={setStatAssistEnabled}
                    />
                  </Space>
                )}
            </Flex>
          </Col>
        </Row>
      </Card>
      <EditSegmentModal
        visible={isEditSegmentModalVisible}
        setVisible={setEditSegmentModalVisible}
        segment={selectedSegment}
        editSegmentName={(id, label) =>
          updatePersistedSegment({
            surveyId,
            segmentId: id,
            data: { label },
          })
        }
        removeSegment={(id) => deletePersistedSegments({ surveyId, segmentIds: [id] })}
      />
      <CreateSegmentModal
        visible={isCreateSegmentModalVisible}
        setVisible={setCreateSegmentModalVisible}
        questions={questions}
        questionResponseOptions={questionResponseOptions}
        saveSegment={handleSaveCustomSegment}
        setSegmentToSave={setSegmentToSave}
      />
      <SaveSegmentModal
        surveyId={surveyId}
        segmentToSave={segmentToSave}
        setVisible={() => setSegmentToSave(undefined)}
        saveSegment={handleSaveCustomSegment}
      />
      <ManageSavedSegmentsModal
        visible={isManageSavedSegmentsModalVisible}
        setVisible={setManageSavedSegmentsModalVisible}
        surveyId={surveyId}
      />
    </div>
  );
}

SegmentThemesCard.propTypes = {
  segments: PropTypes.arrayOf(segmentType),
  questions: PropTypes.arrayOf(questionType.isRequired).isRequired,
  questionResponseOptions: PropTypes.arrayOf(queryType).isRequired,
  selectedQuestion: questionType.isRequired,
  selectedSegmentQuestionId: PropTypes.number,
  setSelectedSegmentQuestionId: PropTypes.func.isRequired,
  statAssistEnabled: PropTypes.bool.isRequired,
  setStatAssistEnabled: PropTypes.func.isRequired,
  createPersistedSegments: PropTypes.func.isRequired,
};

export default SegmentThemesCard;
